// frontend/src/services/api.js
const API_BASE_URL =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:5001";

/**
 * Main API service for trek-related operations
 */
export const trekApi = {
  /**
   * Get all treks
   * @returns {Promise<Array>} Array of all treks
   */
  getAllTreks: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/treks?t=${Date.now()}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Backend returns: { total: number, treks: Array }
      return data.treks || [];
    } catch (error) {
      console.error("Error fetching all treks:", error);
      throw error;
    }
  },

  /**
   * Get single trek by ID/slug
   * @param {string} trekId - Trek ID or slug (e.g., 'everest-base-camp')
   * @returns {Promise<Object>} Single trek object with full details
   */
  getTrekById: async (trekId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/treks/${trekId}`);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Trek "${trekId}" not found`);
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const trek = await response.json();
      return trek;
    } catch (error) {
      console.error(`Error fetching trek ${trekId}:`, error);
      throw error;
    }
  },
};

/**
 * API service for activity/tour operations
 */
export const activityApi = {
  getAllActivities: async (category) => {
    try {
      const url = category
        ? `${API_BASE_URL}/api/activities?category=${encodeURIComponent(category)}&t=${Date.now()}`
        : `${API_BASE_URL}/api/activities?t=${Date.now()}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      return data.activities || [];
    } catch (error) {
      console.error("Error fetching activities:", error);
      throw error;
    }
  },

  getActivityById: async (activityId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/activities/${activityId}`);
      if (!response.ok) {
        if (response.status === 404) throw new Error(`Activity "${activityId}" not found`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching activity ${activityId}:`, error);
      throw error;
    }
  },
};

/**
 * Get live USD/NPR exchange rate
 */
export const getExchangeRate = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/exchange-rate?t=${Date.now()}`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error("Error fetching exchange rate:", error);
    return { rate: 133.5, source: "fallback" };
  }
};

export default trekApi;
